<template>
  <nav class="site-nav" aria-label="Navegación principal">
    <div class="site-nav__inner">
      <router-link
        :to="copyLink('/')"
        class="site-nav__brand"
        aria-label="Prio, inicio"
        @click="closeMobile"
      >
        prio<span class="site-nav__dot" aria-hidden="true">.</span>
      </router-link>
      <div class="site-nav__links">
        <router-link
          v-for="item in items"
          :key="item.to"
          :to="copyLink(item.to)"
          class="site-nav__pill"
          :class="isActive(item.to) ? 'is-active' : ''"
          :aria-current="isActive(item.to) ? 'page' : undefined"
          data-action="nav-link"
          :data-context="item.context"
        >
          {{ item.label }}
        </router-link>
      </div>
      <div class="site-nav__end">
        <router-link
          v-if="isCopyV2"
          :to="originalLink()"
          class="site-nav__original"
          data-action="copy-original"
          >Versión original</router-link
        >
        <a href="https://app.prio.ar" class="site-nav__signin" data-action="nav-login"
          >Iniciar sesión</a
        >
        <a :href="SIGNUP_URL" class="site-nav__cta" data-action="nav-signup">
          <span>Probá gratis</span>
          <span aria-hidden="true">↗</span>
        </a>
        <button
          ref="burgerRef"
          class="site-nav__burger"
          type="button"
          :aria-expanded="mobileOpen"
          aria-controls="mobile-menu"
          aria-label="Abrir menú de navegación"
          data-action="nav-toggle"
          @click="toggleMobileMenu"
        >
          <svg
            v-if="!mobileOpen"
            class="site-nav__burger-icon"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
          <svg
            v-else
            class="site-nav__burger-icon"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
    </div>
    <div v-show="mobileOpen" id="mobile-menu" ref="menuRef" class="site-nav__mobile">
      <div class="site-nav__mobile-list">
        <router-link
          v-for="item in items"
          :key="item.to"
          :to="copyLink(item.to)"
          class="site-nav__mobile-item"
          :class="isActive(item.to) ? 'is-active' : ''"
          :aria-current="isActive(item.to) ? 'page' : undefined"
          data-action="nav-link-mobile"
          :data-context="item.context"
          @click="closeMobile"
        >
          {{ item.label }}
        </router-link>
        <router-link
          v-if="isCopyV2"
          :to="originalLink()"
          class="site-nav__mobile-original"
          data-action="copy-original-mobile"
          @click="closeMobile"
          >Versión original</router-link
        >
        <div class="site-nav__mobile-foot">
          <a
            href="https://app.prio.ar"
            class="site-nav__signin site-nav__signin--center"
            data-action="nav-login-mobile"
            @click="closeMobile"
            >Iniciar sesión</a
          >
          <a
            :href="SIGNUP_URL"
            class="site-nav__cta site-nav__cta--block"
            data-action="nav-signup-mobile"
            @click="closeMobile"
          >
            <span>Probá gratis</span>
            <span aria-hidden="true">↗</span>
          </a>
        </div>
      </div>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch, type ComputedRef } from 'vue';
import { useRoute } from 'vue-router';
import { useCopyVariant } from '@/composables/useCopyVariant';
import { SIGNUP_URL } from '@/constants';

interface NavItem {
  to: string;
  label: string;
  // Stable selector key: keeps the original label even when v2 renames it.
  context: string;
}

const { isCopyV2, copyLink, originalLink } = useCopyVariant();

const items: ComputedRef<NavItem[]> = computed(() => [
  { to: '/', label: 'Inicio', context: 'Inicio' },
  { to: '/consultas', label: 'Consultas', context: 'Consultas' },
  { to: '/presupuestos', label: 'Presupuestos', context: 'Presupuestos' },
  { to: '/proyectos', label: 'Proyectos', context: 'Proyectos' },
  { to: '/catalogo', label: isCopyV2.value ? 'Catálogo' : 'Mis Servicios', context: 'Mis Servicios' },
  { to: '/documentos', label: 'Documentos', context: 'Documentos' },
]);

const route = useRoute();
const mobileOpen = ref(false);
const burgerRef = ref<HTMLButtonElement | null>(null);
const menuRef = ref<HTMLElement | null>(null);

function toggleMobileMenu(): void {
  mobileOpen.value = !mobileOpen.value;
  if (mobileOpen.value) {
    nextTick().then(() => {
      const first = menuRef.value?.querySelector('a');
      (first as HTMLElement | null)?.focus({ preventScroll: true });
    });
  }
}

function closeMobile(): void {
  mobileOpen.value = false;
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape' && mobileOpen.value) {
    closeMobile();
    burgerRef.value?.focus({ preventScroll: true });
  }
}

function isActive(path: string): boolean {
  return route.path === path;
}

// Close the mobile menu on every navigation (back/forward included).
watch(
  () => route.path,
  () => {
    mobileOpen.value = false;
  },
);

onMounted(() => {
  document.addEventListener('keydown', onKeydown);
});

onUnmounted(() => {
  document.removeEventListener('keydown', onKeydown);
});
</script>

<style scoped>
/* Light production navigation: translucent paper over the Light 3 canvas. */
.site-nav {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 50;
  background: rgba(247, 243, 251, 0.86);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border-bottom: 1px solid var(--line);
}
.site-nav__inner {
  width: min(1180px, calc(100% - 40px));
  margin: 0 auto;
  min-height: var(--nav-h);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.site-nav__brand {
  font-family: 'Urbanist', sans-serif;
  font-size: 34px;
  font-weight: 900;
  letter-spacing: -2px;
  line-height: 1;
  color: var(--ink);
}
.site-nav__dot {
  color: var(--pink);
}
.site-nav__links {
  display: none;
  align-items: center;
  gap: 4px;
}
.site-nav__pill {
  padding: 0.5rem 0.75rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 500;
  color: var(--muted);
  white-space: nowrap;
  transition:
    color 0.2s ease,
    background 0.2s ease;
}
.site-nav__pill:hover {
  color: var(--ink);
  background: rgba(101, 65, 189, 0.08);
}
.site-nav__pill.is-active {
  color: #fff;
  font-weight: 600;
  background: var(--violet);
}
.site-nav__end {
  display: flex;
  align-items: center;
  gap: 10px;
}
.site-nav__signin {
  display: none;
  font-size: 0.82rem;
  font-weight: 500;
  color: var(--muted);
}
.site-nav__signin:hover {
  color: var(--ink);
}
.site-nav__original {
  display: none;
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--muted);
  text-decoration: underline;
  text-underline-offset: 3px;
  white-space: nowrap;
}
.site-nav__original:hover {
  color: var(--violet);
}
.site-nav__mobile-original {
  display: flex;
  align-items: center;
  padding: 0.8rem 0.75rem;
  border-radius: 0.75rem;
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--muted);
  text-decoration: underline;
  text-underline-offset: 3px;
}
.site-nav__mobile-original:hover {
  color: var(--violet);
}
.site-nav__signin--center {
  display: block;
  text-align: center;
  padding: 0.7rem;
}
.site-nav__cta {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 0.9rem;
  border-radius: 999px;
  border: 1px solid #b8a5cf;
  background: #fffcfd;
  color: var(--ink);
  font-size: 0.75rem;
  font-weight: 700;
  white-space: nowrap;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
}
.site-nav__cta:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(101, 65, 189, 0.22);
}
.site-nav__cta--block {
  display: inline-flex;
  justify-content: center;
  width: 100%;
  padding: 0.85rem 1.15rem;
}
.site-nav__burger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  min-height: 44px;
  border-radius: 12px;
  color: var(--ink);
}
.site-nav__burger:hover {
  background: rgba(101, 65, 189, 0.08);
}
.site-nav__burger-icon {
  width: 24px;
  height: 24px;
}
.site-nav__mobile {
  border-top: 1px solid var(--line);
  background: rgba(247, 243, 251, 0.98);
}
.site-nav__mobile-list {
  width: min(1180px, calc(100% - 40px));
  margin: 0 auto;
  padding: 12px 0 20px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.site-nav__mobile-item {
  display: flex;
  align-items: center;
  padding: 0.8rem 0.75rem;
  border-radius: 0.75rem;
  font-weight: 500;
  color: var(--muted);
}
.site-nav__mobile-item:hover {
  color: var(--ink);
  background: rgba(101, 65, 189, 0.08);
}
.site-nav__mobile-item.is-active {
  color: var(--violet);
  font-weight: 700;
  background: rgba(101, 65, 189, 0.1);
}
.site-nav__mobile-foot {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 12px;
  padding-top: 16px;
  border-top: 1px solid var(--line);
}
a:focus-visible,
button:focus-visible {
  outline: 3px solid var(--violet);
  outline-offset: 4px;
}

@media (max-width: 379px) {
  .site-nav__cta {
    display: none;
  }
}

@media (min-width: 1100px) {
  .site-nav__links {
    display: flex;
  }
  .site-nav__original {
    display: inline;
  }
  .site-nav__signin {
    display: inline;
  }
  .site-nav__cta {
    padding: 0.6rem 1.15rem;
    font-size: 0.82rem;
  }
  .site-nav__burger {
    display: none;
  }
  .site-nav__mobile {
    display: none;
  }
}
</style>
